/**
 * A Team's backlog filter, as written, both ways.
 *
 * `filterTextOf` reads a stored attachment back as the text a person would
 * have typed, so the preview can ask Jira about it. `attachmentForFilterText`
 * turns typed text into what Save attaches: JQL and issue keys become a Jira
 * query attachment here, and a link goes to `/api/desks/tracker-resolve`,
 * which knows the tracker's names for it (a link to a Jira epic, or to a
 * ticket under one, becomes `parent = EPIC`). Nothing is paraphrased either way.
 */
import { classifyBacklogFilter, collapseJql, formatJiraQueryRef, type DeskAttachmentInput } from "@yagni/shared";

export interface StoredBacklog {
  kind: string;
  source: string;
  ref: string;
  label?: string | null;
}

/** The filter as written for a stored backlog; null when it is not a Jira filter. */
export function filterTextOf(att: StoredBacklog | null | undefined): string | null {
  if (!att || att.source !== "jira") return null;
  if (att.kind === "tracker_view") {
    if (att.ref.startsWith("jql:")) return collapseJql(att.ref.slice("jql:".length)) || null;
    if (att.ref.startsWith("filter:")) {
      const id = att.ref.slice("filter:".length).trim();
      return id ? `filter = ${id}` : null;
    }
    return null;
  }
  if (att.kind === "tracker_project" && att.ref.trim()) return `project = ${att.ref.trim()}`;
  return null;
}

/**
 * A link resolves through the tracker. `unlessEpic` is what a link to one
 * ticket attaches when the tracker finds no epic over it: that ticket, never
 * its whole project.
 */
export type FilterTextAttachment =
  | { attachment: DeskAttachmentInput }
  | { resolve: string; unlessEpic?: DeskAttachmentInput }
  | null;

function keysAttachment(keys: readonly string[]): DeskAttachmentInput {
  return {
    kind: "tracker_view",
    source: "jira",
    ref: formatJiraQueryRef({ jql: `key in (${keys.join(", ")})` }),
    label: null,
  };
}

/** What Save attaches for typed text: an attachment, a link to resolve, or nothing. */
export function attachmentForFilterText(text: string, source: "jira" | "linear"): FilterTextAttachment {
  const reading = classifyBacklogFilter(text, source);
  switch (reading.kind) {
    case "jql":
      return {
        attachment: { kind: "tracker_view", source: "jira", ref: formatJiraQueryRef({ jql: reading.jql }), label: null },
      };
    case "keys":
      return { attachment: keysAttachment(reading.keys) };
    case "issue_link":
      return { resolve: text.trim(), unlessEpic: keysAttachment([reading.key]) };
    case "filter_link":
    case "linear_view":
      return { resolve: text.trim() };
    default:
      return null;
  }
}
